/**
 * AdhocScopeControls - the scope-brief controls on an ad-hoc question card in the deal wizard.
 * Surfaces (and lets the user correct) what the transcript implied about a scope item: whether it's
 * REQUESTED work or only CONTEXT (not costed), the work METHOD, and - data-driven from the shared
 * SCOPE_BRIEF descriptor - the problem, outcome, in/out-of-scope boundary, complexity drivers, and
 * assumptions. AI-prefilled; edits persist to deal_adhoc_field.scope_meta and feed the estimator +
 * grounding.
 *
 * Generic + scalable: the text/tag dimensions render by mapping over SCOPE_BRIEF, so adding a
 * dimension is one descriptor entry + one ScopeMeta field (no bespoke control). Text dimensions get
 * an inline AI "enhance" button (rewrites the user's draft); tag dimensions get a "Suggest" button -
 * both a focused background call, NOT the chat. Drivers also surface the workspace's growing library.
 */

import {
  BPR_VERIFY_IN_ACCOUNT_TOKEN,
  SCOPE_BRIEF,
  SCOPE_EXTRA_SOLUTION_CANDIDATES_KEY,
  SCOPE_STATUS_LABELS,
  SCOPE_STATUS_VALUES,
  WORK_TYPE_OPTIONS,
  parseBprSolutionCandidateBatch,
  solutionCandidatesEnabledForCategory,
  type BprSolutionCandidate,
  type BprSolutionCandidateBatch,
  type PriorityLevel,
  type ScopeBriefKey,
  type ScopeMeta,
  type ScopeStatus,
  type WorkTypeKey,
} from '@protoss/shared';
import { PillRadio, Select, SelectItem, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { SourceQuoteLine } from '../../../components/shared/sales/SourceQuoteLine';
import { salesApi } from '../services/salesApi';
import { useDealWorkspace } from '../dealWorkspaceContext';

// Customer/Recommended priority pills - color-coded by level via the `priority-pills` class.
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// Dedupe the driver-library fetch across the many ad-hoc cards on one deal.
const driverCache = new Map<string, Promise<string[]>>();
function fetchDriverSuggestions(dealUuid: string): Promise<string[]> {
  let p = driverCache.get(dealUuid);
  if (!p) {
    p = salesApi.getScopeDriverSuggestions(dealUuid).catch(() => []);
    driverCache.set(dealUuid, p);
  }
  return p;
}

type BriefValues = Record<string, string | string[]>;

function initValues(meta?: ScopeMeta): BriefValues {
  const out: BriefValues = {};
  for (const d of SCOPE_BRIEF) {
    const raw = meta?.[d.key];
    out[d.key] =
      d.control === 'text' ? (typeof raw === 'string' ? raw : '') : Array.isArray(raw) ? raw : [];
  }
  return out;
}

const PRIMARY_DIMS = SCOPE_BRIEF.filter((d) => d.primary);
const DETAIL_DIMS = SCOPE_BRIEF.filter((d) => !d.primary);

export interface AdhocScopeControlsProps {
  dealUuid: string;
  fieldKey: string;
  scopeMeta?: ScopeMeta;
  disabled?: boolean;
  /** The deal's effective work-type vocabulary (questionnaire summary `workTypeOptions` - the
   *  category's taxonomy profile). Absent => the canonical WORK_TYPE_OPTIONS. */
  workTypeOptions?: Array<{ value: string; label: string; definition: string }>;
}

export function AdhocScopeControls({
  dealUuid,
  fieldKey,
  scopeMeta,
  disabled,
  workTypeOptions,
}: AdhocScopeControlsProps) {
  // Scope status + priorities are prop-controlled (read from scopeMeta) and persist immediately via
  // commitAdhocScopeFields, so a change in the Prioritization pane reflects here live, and vice-versa.
  const scopeStatus: ScopeStatus = scopeMeta?.scopeStatus ?? 'requested';
  const customerPriority = scopeMeta?.customerPriority ?? null;
  const recommendedPriority = scopeMeta?.recommendedPriority ?? null;
  const [workTypes, setWorkTypes] = React.useState<WorkTypeKey[]>(() => {
    const wt = scopeMeta?.workType as unknown;
    if (Array.isArray(wt)) return wt as WorkTypeKey[];
    if (typeof wt === 'string' && wt) return [wt as WorkTypeKey];
    return [];
  });
  const [values, setValues] = React.useState<BriefValues>(() => initValues(scopeMeta));
  const [driverLib, setDriverLib] = React.useState<string[]>([]);
  const [aiSuggestions, setAiSuggestions] = React.useState<Record<string, string[]>>({});
  const [busy, setBusy] = React.useState<ScopeBriefKey | null>(null);
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  // Group (businessProcess) move: null = not creating a new group; '' = new-group input shown.
  const [newGroupName, setNewGroupName] = React.useState<string | null>(null);
  const {
    deal,
    onAdhocScopeChange,
    commitAdhocScopeFields,
    questionnaire,
    handleReloadQuestionnaire,
  } = useDealWorkspace();

  // Categories in SOLUTION_CANDIDATE_CATEGORY_KEYS (BPR, Continuous
  // Improvement) swap the proposedSolution single-shot Suggest for the ranked
  // candidate-solutions flow (grounded in the framework/corpus/transcripts;
  // CI runs engagement-less). The persisted batch lives on scope_meta.extra,
  // so it re-renders after reloads; a regenerate replaces it wholesale.
  const usesSolutionCandidates = solutionCandidatesEnabledForCategory(deal?.dealCategoryKey);
  const [solutionBatch, setSolutionBatch] = React.useState<BprSolutionCandidateBatch | null>(() =>
    parseBprSolutionCandidateBatch(scopeMeta?.extra?.[SCOPE_EXTRA_SOLUTION_CANDIDATES_KEY])
  );
  const [solutionsBusy, setSolutionsBusy] = React.useState(false);
  const [solutionsError, setSolutionsError] = React.useState<string | null>(null);
  const [expandedCandidateId, setExpandedCandidateId] = React.useState<string | null>(null);

  // Re-adopt a NEWER stored batch when the questionnaire reloads (e.g. another
  // surface regenerated); never clobber a fresher in-memory batch with stale meta.
  React.useEffect(() => {
    const stored = parseBprSolutionCandidateBatch(
      scopeMeta?.extra?.[SCOPE_EXTRA_SOLUTION_CANDIDATES_KEY]
    );
    if (!stored) return;
    setSolutionBatch((prev) =>
      !prev || stored.generatedAt >= prev.generatedAt ? stored : prev
    );
  }, [scopeMeta?.extra]);

  const suggestSolutions = async () => {
    setSolutionsBusy(true);
    setSolutionsError(null);
    try {
      const { batch } = await salesApi.suggestSolutionCandidates(dealUuid, fieldKey);
      setSolutionBatch(batch);
      setExpandedCandidateId(null);
    } catch {
      setSolutionsError('Could not generate solution options. Try again.');
    } finally {
      setSolutionsBusy(false);
    }
  };

  // Effective work-type vocabulary: explicit prop > the questionnaire summary's taxonomy-profile
  // options (already in context) > the canonical defaults.
  const effectiveWorkTypeOptions =
    workTypeOptions ?? questionnaire?.workTypeOptions ?? WORK_TYPE_OPTIONS;

  const currentGroup = scopeMeta?.businessProcess?.trim() ?? '';
  // Distinct coarse groups already on this deal (the move-target options).
  const existingGroups = React.useMemo(() => {
    const names = new Set<string>();
    for (const s of questionnaire?.sections ?? []) {
      for (const g of s.groups) {
        const bp = g.fields.find((f) => f.isAdhocPrimary)?.scopeMeta?.businessProcess?.trim();
        if (bp) names.add(bp);
      }
    }
    if (currentGroup) names.add(currentGroup);
    return [...names].sort();
  }, [questionnaire, currentGroup]);

  // Move this scope item to a group (or ungroup with ''); persists then reloads so the questionnaire
  // re-splits into peer sections. The route records the regrouping to the flywheel.
  const moveToGroup = React.useCallback(
    async (bp: string) => {
      if (!dealUuid || bp === currentGroup) return;
      await salesApi.mergeAdhocScopeMeta(dealUuid, fieldKey, { businessProcess: bp });
      await handleReloadQuestionnaire();
    },
    [dealUuid, fieldKey, currentGroup, handleReloadQuestionnaire]
  );

  React.useEffect(() => {
    let cancelled = false;
    void fetchDriverSuggestions(dealUuid).then((s) => {
      if (!cancelled) setDriverLib(s);
    });
    return () => {
      cancelled = true;
    };
  }, [dealUuid]);

  const textVal = (k: string): string => {
    const v = values[k];
    return typeof v === 'string' ? v : '';
  };
  const tagVal = (k: string): string[] => {
    const v = values[k];
    return Array.isArray(v) ? v : [];
  };

  // Record a STAGED brief/workType edit (optimistic questionnaire update + arms Save); persisted on
  // Save. NOTE: scopeStatus + priorities are NOT here - they persist immediately via
  // commitAdhocScopeFields (the staged Save preserves them; see handleAdhocScopeChange).
  const persist = React.useCallback(
    (types: WorkTypeKey[], vals: BriefValues) => {
      const meta: Record<string, unknown> = {};
      if (types.length > 0) meta.workType = types;
      for (const d of SCOPE_BRIEF) {
        const v = vals[d.key];
        if (d.control === 'text') {
          const t = (typeof v === 'string' ? v : '').trim();
          if (t) meta[d.key] = t;
        } else {
          const arr = Array.isArray(v) ? v.filter(Boolean) : [];
          if (arr.length > 0) meta[d.key] = arr;
        }
      }
      onAdhocScopeChange(fieldKey, meta as ScopeMeta);
    },
    [fieldKey, onAdhocScopeChange]
  );

  const onStatus = (v: string | null) => {
    commitAdhocScopeFields(fieldKey, {
      scopeStatus: v === 'mentioned' ? 'mentioned' : v === 'ongoing' ? 'ongoing' : 'requested',
    });
  };
  const toggleWorkType = (t: WorkTypeKey) => {
    const next = workTypes.includes(t) ? workTypes.filter((x) => x !== t) : [...workTypes, t];
    setWorkTypes(next);
    persist(next, values);
  };

  const setText = (k: string, text: string) => setValues((p) => ({ ...p, [k]: text }));
  const commitText = () => persist(workTypes, values);

  const addTag = (k: string, raw: string) => {
    const t = raw.trim();
    setDrafts((p) => ({ ...p, [k]: '' }));
    if (!t || tagVal(k).includes(t)) return;
    const next = { ...values, [k]: [...tagVal(k), t] };
    setValues(next);
    persist(workTypes, next);
  };
  const removeTag = (k: string, t: string) => {
    const next = { ...values, [k]: tagVal(k).filter((x) => x !== t) };
    setValues(next);
    persist(workTypes, next);
  };

  const enhanceText = async (k: ScopeBriefKey) => {
    setBusy(k);
    try {
      const res = await salesApi.enhanceScopeText(dealUuid, fieldKey, k, textVal(k));
      if (res.text) {
        const next = { ...values, [k]: res.text };
        setValues(next);
        persist(workTypes, next);
      }
    } catch {
      /* best-effort assist - leave the field untouched on failure */
    } finally {
      setBusy(null);
    }
  };

  const suggestTags = async (k: ScopeBriefKey) => {
    setBusy(k);
    try {
      const res = await salesApi.enhanceScopeText(dealUuid, fieldKey, k, tagVal(k).join(', '));
      if (res.suggestions && res.suggestions.length > 0) {
        setAiSuggestions((p) => ({ ...p, [k]: res.suggestions as string[] }));
      }
    } catch {
      /* best-effort assist */
    } finally {
      setBusy(null);
    }
  };

  // "Use this solution": the approach text lands in the textarea through the
  // EXISTING staging (persists on Save, which also queues the section's
  // future-state diagram refresh); the pick marker itself persists immediately
  // (best-effort), mirroring the scopeStatus-vs-brief split.
  const pickCandidate = (candidate: BprSolutionCandidate) => {
    const next = { ...values, proposedSolution: candidate.approach };
    setValues(next);
    persist(workTypes, next);
    setSolutionBatch((prev) => (prev ? { ...prev, pickedCandidateId: candidate.id } : prev));
    void salesApi.pickSolutionCandidate(dealUuid, fieldKey, candidate.id).catch(() => {});
  };

  // Highlight the literal VERIFY_IN_ACCOUNT token so it visibly demands action.
  const renderVerifyText = (text: string): React.ReactNode => {
    const parts = text.split(BPR_VERIFY_IN_ACCOUNT_TOKEN);
    if (parts.length === 1) return text;
    return parts.flatMap((part, i) =>
      i === 0
        ? [part]
        : [
            <mark key={`v${i}`} className="adhoc-solutions__verify">
              {BPR_VERIFY_IN_ACCOUNT_TOKEN}
            </mark>,
            part,
          ]
    );
  };

  const renderCandidateList = (label: string, items: string[]) =>
    items.length > 0 ? (
      <div className="adhoc-solutions__detail">
        <span className="adhoc-solutions__detail-label">{label}</span>
        <ul className="adhoc-solutions__detail-list">
          {items.map((item, i) => (
            <li key={i}>{renderVerifyText(item)}</li>
          ))}
        </ul>
      </div>
    ) : null;

  const renderSolutionCandidates = () => {
    if (!solutionsBusy && !solutionsError && !solutionBatch) return null;
    return (
      <div className="adhoc-solutions">
        {solutionsError ? (
          <span className="adhoc-solutions__error">{solutionsError}</span>
        ) : null}
        {solutionsBusy ? (
          <div className="adhoc-solutions__skeleton" aria-hidden="true">
            <span className="adhoc-solutions__skeleton-line" />
            <span className="adhoc-solutions__skeleton-line adhoc-solutions__skeleton-line--short" />
          </div>
        ) : null}
        {!solutionsBusy && solutionBatch
          ? solutionBatch.candidates.map((c) => {
              const expanded = expandedCandidateId === c.id;
              const picked = solutionBatch.pickedCandidateId === c.id;
              const cardClass = [
                'adhoc-solutions__card',
                c.needsReview ? 'adhoc-solutions__card--review' : '',
                picked ? 'adhoc-solutions__card--picked' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div key={c.id} className={cardClass}>
                  <div className="adhoc-solutions__card-head">
                    <button
                      type="button"
                      className="adhoc-solutions__expand"
                      aria-expanded={expanded}
                      onClick={() => setExpandedCandidateId(expanded ? null : c.id)}
                    >
                      <span className="adhoc-solutions__rank">#{c.rank}</span>
                      <span className="adhoc-solutions__title">{c.title}</span>
                    </button>
                    {!disabled ? (
                      <button
                        type="button"
                        className="adhoc-solutions__use"
                        onClick={() => pickCandidate(c)}
                      >
                        {picked ? 'Selected' : 'Use this solution'}
                      </button>
                    ) : null}
                  </div>
                  <div className="adhoc-solutions__pills">
                    <span className={`adhoc-solutions__pill adhoc-solutions__pill--risk-${c.risk}`}>
                      Risk: {c.risk}
                    </span>
                    <span className="adhoc-solutions__pill">Effort: {c.effort}</span>
                    <span className="adhoc-solutions__pill">
                      {Math.round(c.confidence * 100)}% confident
                    </span>
                    {c.needsReview ? (
                      <span className="adhoc-solutions__pill adhoc-solutions__pill--review">
                        Needs review
                      </span>
                    ) : c.grounded ? (
                      <span className="adhoc-solutions__pill adhoc-solutions__pill--grounded">
                        Grounded
                      </span>
                    ) : null}
                  </div>
                  {!expanded ? (
                    <p className="adhoc-solutions__summary">{c.approach}</p>
                  ) : (
                    <div className="adhoc-solutions__body">
                      {c.needsReview ? (
                        <p className="adhoc-solutions__review-note">
                          Low confidence - verify with a solution architect before committing.
                        </p>
                      ) : null}
                      <p className="adhoc-solutions__approach">{renderVerifyText(c.approach)}</p>
                      {renderCandidateList('Pros', c.pros)}
                      {renderCandidateList('Cons', c.cons)}
                      {renderCandidateList('Prerequisites', c.prerequisites)}
                      {renderCandidateList('Verification steps', c.verificationSteps)}
                      {c.destructiveSteps.length > 0 ? (
                        <div className="adhoc-solutions__detail">
                          <span className="adhoc-solutions__detail-label">
                            Changes to the account
                            <span className="adhoc-solutions__pill adhoc-solutions__pill--review">
                              Sandbox first
                            </span>
                          </span>
                          <ul className="adhoc-solutions__detail-list">
                            {c.destructiveSteps.map((step, i) => (
                              <li key={i}>{renderVerifyText(step)}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {c.sources.length > 0 ? (
                        <div className="adhoc-solutions__detail">
                          <span className="adhoc-solutions__detail-label">Sources</span>
                          <ul className="adhoc-solutions__detail-list adhoc-solutions__sources">
                            {c.sources.map((sourceId) => {
                              const ref = solutionBatch.groundingRefs.find(
                                (r) => r.id === sourceId
                              );
                              return <li key={sourceId}>{ref?.title ?? ref?.patternKey ?? sourceId}</li>;
                            })}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })
          : null}
        {!solutionsBusy && solutionBatch ? (
          <span className="adhoc-solutions__meta">
            Generated {new Date(solutionBatch.generatedAt).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
            {solutionBatch.frameworkVersionUuid ? ' - grounded in the pinned framework' : ''}
          </span>
        ) : null}
      </div>
    );
  };

  // Suggestions for a tag dimension: driver library (drivers only) + AI suggestions, minus current.
  const suggestionsFor = (k: ScopeBriefKey, source?: 'drivers'): string[] => {
    const cur = new Set(tagVal(k));
    const pool = [...(source === 'drivers' ? driverLib : []), ...(aiSuggestions[k] ?? [])];
    return [...new Set(pool)].filter((s) => !cur.has(s)).slice(0, 8);
  };

  const hasDetailContent = DETAIL_DIMS.some((d) =>
    d.control === 'text' ? textVal(d.key).trim().length > 0 : tagVal(d.key).length > 0
  );

  const renderTags = (d: (typeof SCOPE_BRIEF)[number]) => {
    const tags = tagVal(d.key);
    const suggestions = suggestionsFor(d.key, d.suggestionSource);
    return (
      <div className="adhoc-scope__field" key={d.key}>
        <div className="adhoc-scope__label-row">
          <span className="adhoc-scope__label">{d.label}</span>
          {!disabled ? (
            <button
              type="button"
              className="adhoc-scope__ai-btn"
              onClick={() => void suggestTags(d.key)}
              disabled={busy === d.key}
            >
              {busy === d.key ? 'Thinking…' : 'Suggest'}
            </button>
          ) : null}
        </div>
        <div className="adhoc-scope__chips">
          {tags.map((t) => (
            <span key={t} className="adhoc-scope__chip">
              {t}
              <button
                type="button"
                className="adhoc-scope__chip-remove"
                aria-label={`Remove ${t}`}
                onClick={() => removeTag(d.key, t)}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
          {!disabled ? (
            <input
              className="adhoc-scope__chip-input"
              value={drafts[d.key] ?? ''}
              placeholder={tags.length === 0 ? d.placeholder : ''}
              onChange={(e) => setDrafts((p) => ({ ...p, [d.key]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag(d.key, drafts[d.key] ?? '');
                }
              }}
              onBlur={() => {
                if ((drafts[d.key] ?? '').trim()) addTag(d.key, drafts[d.key] ?? '');
              }}
            />
          ) : null}
        </div>
        {suggestions.length > 0 && !disabled ? (
          <div className="adhoc-scope__suggestions">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="adhoc-scope__suggestion"
                onClick={() => addTag(d.key, s)}
              >
                + {s}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderText = (d: (typeof SCOPE_BRIEF)[number]) => {
    // BPR deals: the Proposed solution field's assist is the ranked
    // candidate-solutions flow (replaces the single-shot rewrite); everything
    // else keeps the generic enhance.
    const usesCandidates = d.key === 'proposedSolution' && usesSolutionCandidates;
    return (
      <div className="adhoc-scope__field" key={d.key}>
        <div className="adhoc-scope__label-row">
          <span className="adhoc-scope__label">{d.label}</span>
          {!disabled && d.enhanceable ? (
            usesCandidates ? (
              <button
                type="button"
                className="adhoc-scope__ai-btn"
                onClick={() => void suggestSolutions()}
                disabled={solutionsBusy}
              >
                {solutionsBusy ? 'Thinking...' : solutionBatch ? 'Regenerate solutions' : 'Suggest solutions'}
              </button>
            ) : (
              <button
                type="button"
                className="adhoc-scope__ai-btn"
                onClick={() => void enhanceText(d.key)}
                disabled={busy === d.key}
              >
                {busy === d.key ? 'Thinking...' : 'Suggest'}
              </button>
            )
          ) : null}
        </div>
        <div className="adhoc-scope__text-wrap">
          <textarea
            className="adhoc-scope__textarea"
            value={textVal(d.key)}
            placeholder={d.placeholder}
            rows={2}
            disabled={disabled}
            onChange={(e) => setText(d.key, e.target.value)}
            onBlur={commitText}
          />
        </div>
        {d.help ? <span className="adhoc-scope__help">{d.help}</span> : null}
        {usesCandidates ? renderSolutionCandidates() : null}
      </div>
    );
  };

  const renderDim = (d: (typeof SCOPE_BRIEF)[number]) =>
    d.control === 'text' ? renderText(d) : renderTags(d);

  return (
    <div className="adhoc-scope">
      {/* Persisted transcript provenance ("where did this come from") - server-verified verbatim
          quote(s) stamped by discovery, with the source document per quote when recorded
          (sourceRefs); absent (no line) when the item has no verified quote. */}
      <SourceQuoteLine quote={scopeMeta?.sourceQuote} refs={scopeMeta?.sourceRefs} />
      <div className="adhoc-scope__priorities">
        <div className="adhoc-scope__field">
          <span className="adhoc-scope__label">Customer priority</span>
          <PillRadio
            options={PRIORITY_OPTIONS}
            selectedValue={customerPriority}
            onSelectionChange={(v) => {
              if (v) commitAdhocScopeFields(fieldKey, { customerPriority: v as PriorityLevel });
            }}
            disabled={disabled}
            className="priority-pills"
          />
        </div>
        <div className="adhoc-scope__field">
          <span className="adhoc-scope__label">Recommended priority</span>
          <PillRadio
            options={PRIORITY_OPTIONS}
            selectedValue={recommendedPriority}
            onSelectionChange={(v) => {
              if (v) commitAdhocScopeFields(fieldKey, { recommendedPriority: v as PriorityLevel });
            }}
            disabled={disabled}
            className="priority-pills"
          />
        </div>
      </div>
      <div className="adhoc-scope__field">
        <Tooltip.Simple label={scopeMeta?.statusReason ?? null}>
          <span className="adhoc-scope__label">Scope</span>
        </Tooltip.Simple>
        <PillRadio
          options={SCOPE_STATUS_VALUES.map((v) => ({ value: v, label: SCOPE_STATUS_LABELS[v] }))}
          selectedValue={scopeStatus}
          onSelectionChange={onStatus}
          disabled={disabled}
        />
      </div>
      <div className="adhoc-scope__field">
        <span className="adhoc-scope__label">Functional area</span>
        {newGroupName === null ? (
          <Select
            value={currentGroup || '__ungrouped__'}
            onValueChange={(v) => {
              if (v === '__new__') setNewGroupName('');
              else void moveToGroup(v === '__ungrouped__' ? '' : v);
            }}
            size="sm"
            disabled={disabled}
          >
            <SelectItem value="__ungrouped__">Ungrouped</SelectItem>
            {existingGroups.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
            <SelectItem value="__new__">+ New area…</SelectItem>
          </Select>
        ) : (
          <div className="adhoc-scope__chips">
            <input
              className="adhoc-scope__chip-input"
              autoFocus
              value={newGroupName}
              placeholder="New area name"
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const name = newGroupName.trim();
                  setNewGroupName(null);
                  if (name) void moveToGroup(name);
                } else if (e.key === 'Escape') {
                  setNewGroupName(null);
                }
              }}
              onBlur={() => {
                const name = (newGroupName ?? '').trim();
                setNewGroupName(null);
                if (name) void moveToGroup(name);
              }}
            />
          </div>
        )}
      </div>
      <div className="adhoc-scope__field">
        <span className="adhoc-scope__label">Activities</span>
        <div className="adhoc-scope__work-types">
          {effectiveWorkTypeOptions.map((o) => {
            const active = workTypes.includes(o.value);
            return (
              <Tooltip.Simple key={o.value} label={o.definition} side="bottom">
                <button
                  type="button"
                  className={`adhoc-scope__work-type-pill${active ? ' adhoc-scope__work-type-pill--active' : ''}`}
                  onClick={() => toggleWorkType(o.value)}
                  disabled={disabled}
                >
                  {o.label}
                </button>
              </Tooltip.Simple>
            );
          })}
        </div>
      </div>

      {PRIMARY_DIMS.map(renderDim)}

      <details className="adhoc-scope__details" open={hasDetailContent}>
        <summary className="adhoc-scope__details-summary">Scope details</summary>
        <div className="adhoc-scope__details-body">{DETAIL_DIMS.map(renderDim)}</div>
      </details>
    </div>
  );
}
